// /pricing-catalogues - the Pricing Catalogs page with the estimating database
// in it (owner 2026-11-03: "make sure the pricing catalogs are merged with
// estimation database ... and after merge the estimation database is
// removed").
//
// One page, one tab per table: Catalogs (the catalog tree, pricingDatabaseView)
// and the database tables - materials and formulas, labor and crews, tools and
// equipment, catalog mapping, general conditions and the rest
// (estimatingDatabase; owner kept both keys). All of them read and write the
// same CurLight collections they always did, company rows over BidLight's.
//
// The tab is in the address (?tab=labor), so a link opens the right table, and
// the old /database/<page> addresses redirect here (App.jsx).
//
// Laid out as the desktop estimating app's database editor
// (DatabaseTabPage.xaml): a left list of the tables in the desktop's order,
// the table beside it (owner 2026-11-03: "the full design from the desktop
// app and style and fonts"). Styles: pricingWorkspace.scss.
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { canOpenEstimatingDatabase } from "components/EstimatingDatabase/access";
import { DATABASE_TABS } from "pages/EstimatingDatabase";
import PricingCatelogs from "./PricingCatelogs";
import "./pricingWorkspace.scss";

export const CATALOGS_TAB = "catalogs";

// DatabaseCategory.cs order; the desktop's names.
const DESKTOP_ORDER = [
  ["catalog-mapping", "Catalog Mapping"],
  [CATALOGS_TAB, "Catalog"],
  ["materials", "Material & Formula"],
  ["labor", "Labor"],
  ["tools", "Equipment"],
  ["special-allowances", "Special Allowances"],
  ["survey-layout", "Survey and Layout"],
  ["project-requirements", "Project Requirements"],
  ["general-conditions", "General Conditions"],
  ["insurance-contingency", "Insurance Contingencies"],
  ["clarifications", "Clarifications"],
  ["material-classes", "Material Class"],
  ["awp", "Advanced Work Packages"],
  ["labor-types", "Labor Types"],
  ["project-types", "Project Types"],
];
const ORDER = DESKTOP_ORDER.map(([k]) => k);
const NAME = Object.fromEntries(DESKTOP_ORDER);

// The tabs this person can open, in order: [{ key, label, Component }].
export function workspaceTabs(permissions) {
  const tabs = [];
  if (Array.isArray(permissions) && permissions.includes(ALL_PERMISSIONS.PRICING_DATABASE_VIEW)) {
    tabs.push({ key: CATALOGS_TAB, label: NAME[CATALOGS_TAB], Component: PricingCatelogs });
  }
  if (canOpenEstimatingDatabase(permissions)) {
    DATABASE_TABS.forEach((t) => tabs.push({ key: t.key, label: NAME[t.key] || t.title, Component: t.component }));
  }
  const rank = (k) => (ORDER.includes(k) ? ORDER.indexOf(k) : ORDER.length);
  return tabs.sort((a, b) => rank(a.key) - rank(b.key));
}

export default function PricingCatalogsWorkspace() {
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = allPermissionRedux
    ?.filter((element) => companyPermissionRedux?.includes(element))
    ?.filter((i) => usersPermissionRedux?.includes(i));
  const history = useHistory();
  const location = useLocation();

  const tabs = useMemo(() => workspaceTabs(FinalPermissions), [FinalPermissions]);
  const asked = new URLSearchParams(location.search || "").get("tab");
  // the catalog opens first when the person has it, as before the merge
  const fallback = (tabs.find((t) => t.key === CATALOGS_TAB) || tabs[0] || {}).key;
  const active = tabs.find((t) => t.key === asked) ? asked : fallback;
  const Active = (tabs.find((t) => t.key === active) || {}).Component;

  if (!tabs.length) return null;
  return (
    <div className="pricing-workspace" data-testid="pricing-workspace">
      <nav className="pw-nav" aria-label="Pricing database">
        <div className="pw-nav-head">
          <svg width="25" height="25" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#fff" d="M12 3C7.58 3 4 4.79 4 7v10c0 2.21 3.59 4 8 4s8-1.79 8-4V7c0-2.21-3.58-4-8-4zm6 14c0 .5-2.13 2-6 2s-6-1.5-6-2v-2.23c1.61.78 3.72 1.23 6 1.23s4.39-.45 6-1.23V17zm0-4.55c-1.3.95-3.58 1.55-6 1.55s-4.7-.6-6-1.55V9.64c1.47.83 3.61 1.36 6 1.36s4.53-.53 6-1.36v2.81zM12 9C8.13 9 6 7.5 6 7s2.13-2 6-2 6 1.5 6 2-2.13 2-6 2z" />
          </svg>
          <span>Personal Database</span>
        </div>
        <ul>
          {tabs.map(({ key, label }) => (
            <li key={key}>
              <button
                type="button"
                className={key === active ? "pw-nav-item pw-nav-active" : "pw-nav-item"}
                aria-current={key === active ? "page" : undefined}
                onClick={() => history.replace({ pathname: location.pathname, search: `?tab=${key}` })}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <main className="pw-main" key={active}>
        {Active ? <Active /> : null}
      </main>
    </div>
  );
}
